import { IconLogin } from "@/assets/icons/IconLogin";
import { Button } from "@/components/elements/Button";
import { Checkbox } from "@/components/elements/Checkbox";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { loginSchema } from "@/features/auth/schema/loginSchema";
import { loginApi } from "@/features/auth/services/loginApi";
import { TLoginRequest } from "@/features/auth/types/loginRequest";
import { useCacheLocal } from "@/hooks/useCacheLocal";
import { AppRoutes } from "@/routes/config";
import { setErrorForm, showError } from "@/utils/error";
import { pick } from "@/utils/object";
import { getQueryFromURL } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack } from "@mui/material";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

type TQuery = {
  email: string;
  redirectUrl: string;
};

export const LoginForm = () => {
  const cacheLocal = useCacheLocal("login-form");
  const navigate = useNavigate();
  const query = getQueryFromURL<TQuery>();

  const [cached, setCached] = useState(!!cacheLocal.getJSON()?.email);

  const defaultValues = useMemo(() => {
    if (query && query.email) return { email: query.email };
    if (cacheLocal.getJSON()) return pick(cacheLocal.getJSON(), "email");
  }, [query, cacheLocal]);

  const methods = useForm<TLoginRequest>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(loginSchema),
  });
  const { control } = methods;

  const onSubmit = async (data: TLoginRequest) => {
    try {
      await loginApi(data);
      if (cached) {
        cacheLocal.setJSON(pick(data, "email"));
      } else {
        cacheLocal.remove();
      }

      if (query && query.redirectUrl) {
        navigate(query.redirectUrl);
      } else {
        navigate(AppRoutes.home);
      }
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  const handleToggleCache = (_: unknown, checked: boolean) => {
    setCached(checked);
  };

  return (
    <Form methods={methods} onSubmit={onSubmit} enableBlocker={false} schema={loginSchema}>
      <Stack gap={3} width={400}>
        <FormField control={control} name="email">
          <Input size="lg" placeholder="info@irohana.co.jp" />
        </FormField>
        <FormField control={control} name="password">
          <Input type="password" size="lg" />
        </FormField>
        <Checkbox onChange={handleToggleCache} checked={cached} label="メールアドレスを保存する" />
        <Button type="submit" size="sm" fullWidth startIcon={<IconLogin />}>
          ログイン
        </Button>
      </Stack>
    </Form>
  );
};
